import React from "react";
import { formatPrice, siteConfig } from "../Datas/productData";
import PolicyNav, { POLICY_UPDATED } from "./PolicyNav";
import "../css/privacy.scss";

const faqGroups = [
  {
    title: "Orders",
    items: [
      [
        "How do I know you received my order?",
        "After payment, you should see an order confirmation and receive an email at the address used during checkout. Check spam or junk folders before contacting us with the payment reference.",
      ],
      [
        "Can I change or cancel an order?",
        "Email us immediately. We will try to help before processing begins, but changes and cancellations are not guaranteed once an order is being prepared or has been dispatched.",
      ],
      [
        "What if part of my order is unavailable?",
        "We may contact you to offer a suitable option or cancel the affected item. Any amount charged for a cancelled item will be returned to the original payment method.",
      ],
    ],
  },
  {
    title: "Shipping & delivery",
    items: [
      [
        "How much is shipping?",
        `Tracked shipping is ${formatPrice(siteConfig.shipping.standardRate)} per order for destinations currently available at checkout.`,
      ],
      [
        "When should my order arrive?",
        "Orders are normally prepared in 1–2 business days. Typical delivery is 5–12 business days within the United States and 7–21 business days for other available destinations after dispatch.",
      ],
      [
        "Where is my tracking update?",
        "The first carrier scan can take up to 48 hours after dispatch. If tracking has not moved for seven business days, email us with your order number.",
      ],
      [
        "What happens after a missed delivery?",
        "Follow the carrier notice to arrange another attempt or collection. A parcel returned after missed attempts may require payment of actual reshipping costs.",
      ],
      [
        "Will I pay customs duties?",
        "International recipients are responsible for import duty, tax, brokerage, or customs charges unless checkout expressly says those charges are included.",
      ],
    ],
  },
  {
    title: "Products",
    items: [
      [
        "Where can I find size, materials, and care details?",
        "Each product page lists the available variants, dimensions, materials, features, and care guidance. Email us before ordering if a detail you need is missing.",
      ],
      [
        "Why does the color look slightly different in person?",
        "Screen settings and lighting affect color. Material texture and normal production variation can also create small differences without making an item defective.",
      ],
      [
        "Will a sold-out product return?",
        "Availability can change, but a restock is not guaranteed. Customer care can tell you whether we currently expect more inventory.",
      ],
    ],
  },
  {
    title: "Payments & pricing",
    items: [
      [
        "Which payment methods can I use?",
        "The secure hosted payment page displays the payment methods currently available for your order. Major cards may be offered, but availability can vary by provider and destination.",
      ],
      [
        "Does tiporchid store my card number?",
        "No. Card details are entered on the hosted payment provider’s page. This storefront receives payment status and limited transaction information, not the full card number or security code.",
      ],
      [
        "What currency are prices shown in?",
        "Prices on this storefront are in U.S. dollars. Your bank may apply its own conversion rate or international transaction fee if your account uses another currency.",
      ],
      [
        "Why did my discount code not work?",
        "Check its spelling, dates, eligible products, region, minimum spend, and whether it can be combined with another offer. A code cannot normally be added after an order is placed.",
      ],
    ],
  },
  {
    title: "Returns & refunds",
    items: [
      [
        "What is the return window?",
        "Contact us within 30 calendar days after delivery. Eligible goods must be unused, unwashed, and returned with their original tags, accessories, and packaging.",
      ],
      [
        "Do you offer exchanges?",
        "We do not currently process direct exchanges. Return an eligible item and place a new order for the preferred option.",
      ],
      [
        "Who pays return shipping?",
        "You pay return postage for a change-of-mind return. If we confirm that an item was damaged, defective, or incorrect, we will provide or reimburse a reasonable standard return method.",
      ],
      [
        "When will my refund appear?",
        "We normally inspect a received return within seven business days. After we issue an approved refund, a bank or card provider may need another 5–10 business days to post it.",
      ],
    ],
  },
  {
    title: "Wholesale & corporate enquiries",
    items: [
      [
        "Can I ask about wholesale or corporate gifting?",
        `Yes. Email ${siteConfig.contact.email} with your organization, destination, preferred products, quantity, and required date. Availability and commercial terms are confirmed case by case.`,
      ],
    ],
  },
];

export default function Faqs() {
  return (
    <main className="policy-page privacy">
      <header className="policy-hero">
        <p className="policy-eyebrow">Customer care</p>
        <h1>Frequently asked questions</h1>
        <p>Answers about orders, delivery, products, payment, and returns.</p>
        <span>Last updated {POLICY_UPDATED}</span>
      </header>

      <div className="policy-layout">
        <PolicyNav current="/faqs" />

        <article className="policy-content">
          {faqGroups.map((group, groupIndex) => (
            <section className="policy-section" key={group.title}>
              <div className="policy-section__heading">
                <span>{String(groupIndex + 1).padStart(2, "0")}</span>
                <h2>{group.title}</h2>
              </div>
              <div className="policy-faq-list">
                {group.items.map(([question, answer]) => (
                  <details key={question}>
                    <summary>{question}</summary>
                    <p>{answer}</p>
                  </details>
                ))}
              </div>
            </section>
          ))}

          <section className="policy-help" aria-labelledby="faq-help">
            <div>
              <p className="policy-eyebrow">Still need help?</p>
              <h2 id="faq-help">Talk to customer care.</h2>
            </div>
            <a href={`mailto:${siteConfig.contact.email}`}>
              {siteConfig.contact.email} <span aria-hidden="true">↗</span>
            </a>
          </section>
        </article>
      </div>
    </main>
  );
}
